import { useCallback, useEffect, useRef } from "react";
import { getErrorMessage } from "../lib/api";

const DISMISS_AFTER = 4000;

/**
 * Wraps the `setNotification` prop that every page threads around.
 *
 * Each call site used to inline `setTimeout(..., 3000)` and never store the
 * handle, so two notifications within the timeout window let the first timer
 * hide the second one early, and a timer that fired after navigation set
 * state on an unmounted component.
 */
export default function useNotify(setNotification) {
    const timerRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const show = useCallback((type, text) => {
        if (!text || !mountedRef.current) return;

        // Restarting the timer means the newest message always gets its full
        // time on screen.
        if (timerRef.current) clearTimeout(timerRef.current);

        setNotification({ show: true, type, text });

        timerRef.current = setTimeout(() => {
            if (mountedRef.current) setNotification({ show: false, type: "", text: "" });
        }, DISMISS_AFTER);
    }, [setNotification]);

    const success = useCallback((text) => show("success", text), [show]);

    // Silent for aborted requests: getErrorMessage returns null for those.
    const error = useCallback((err, fallback) => {
        const text = getErrorMessage(err, fallback);
        if (text) show("error", text);
    }, [show]);

    return { success, error, show };
}
